/**
 * Chapter Info Drawer
 * Shows surah description, revelation info, and source
 */

import { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { BookOpen } from 'lucide-react';
import { getChapterInfo } from '@/services/quran.service';
import type { ChapterInfo, SurahListItem } from '@/types/quran';

interface ChapterInfoDrawerProps {
  open: boolean;
  onClose: () => void;
  surah: SurahListItem | null;
}

export function ChapterInfoDrawer({ open, onClose, surah }: ChapterInfoDrawerProps) {
  const [info, setInfo] = useState<ChapterInfo | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && surah) {
      setLoading(true);
      getChapterInfo(surah.number)
        .then(setInfo)
        .catch(() => setInfo(null))
        .finally(() => setLoading(false));
    }
  }, [open, surah]);

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto rounded-t-2xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            {surah ? `${surah.name} — ${surah.englishName}` : 'ข้อมูลซูเราะฮ์'}
          </SheetTitle>
          <SheetDescription>
            {surah && (
              <span>
                ซูเราะฮ์ที่ {surah.number} · {surah.numberOfAyahs} อายะฮ์ ·{' '}
                {surah.revelationType === 'Meccan' ? 'มักกะฮ์' : 'มะดีนะฮ์'}
              </span>
            )}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-4/6" />
            </div>
          ) : info ? (
            <>
              {info.shortText && (
                <p className="text-sm text-muted-foreground italic">
                  {info.shortText}
                </p>
              )}
              <div
                className="prose prose-sm dark:prose-invert max-w-none text-sm leading-relaxed"
                dangerouslySetInnerHTML={{ __html: info.text }}
              />
              {info.source && (
                <p className="text-xs text-muted-foreground/60 pt-2 border-t">
                  แหล่งที่มา: {info.source}
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">ไม่พบข้อมูลซูเราะฮ์</p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default ChapterInfoDrawer;
